import { MovableDialog } from '@/components/shared/MovableDialog';
import { applicationIdentityKey } from '@shared/applicationIdentity';
import { Fragment, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v2, ApiError, type Submission, type AttentionItem } from '@/api/v2Client';
import { AttentionSurface } from '@/components/dashboard/AttentionSurface';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Pagination } from '@/components/dashboard/Pagination';
import { SubmissionGroupRow } from '@/components/dashboard/SubmissionGroupRow';
import { SubmissionSubRow } from '@/components/dashboard/SubmissionSubRow';
import { ReviewClassificationsCTA } from '@/components/dashboard/ReviewClassificationsCTA';
import { ReorderModal } from '@/components/home/ReorderModal';
import { LifecycleReportPanel } from '@/components/dashboard/LifecycleReportPanel';
import { StatusDrawer } from '@/components/dashboard/StatusDrawer';
import { ParallelGroupRow } from '@/components/dashboard/ParallelGroupRow';
import { REGIONS } from '@/constants/regions';
import { STATUS_CONFIG, getStatusConfig, isSequenceLocked, normalizeStatus } from '@/constants/status';
import { useStatusEvidence } from '@/hooks/useStatusEvidence';
import { subscribeSubmissionRefresh } from '@/lib/submissionRefresh';
import { nextStepFor } from '@/lib/nextStep';
import { computeParentFace } from '@/lib/parentFace';

// phase-dashboard-coherence item 2 — the private isUnlockedSequence mirror
// that lived here is GONE: it had drifted from the server rule exactly like
// the ReorderModal's copy before phase-dashboard-lock-consistency (it missed
// Submitted / Approved / Rejected / PendingAgencyConfirmation), so an
// all-Submitted group still offered the ↕ reorder arrow. The one shared
// predicate (isSequenceLocked) now decides, same as the padlocks and the
// reorder dialog.

const DASH_PAGE_SIZE = 25;
const COMPACT_LS_KEY = 'dashboardCompactMode';

type SortCol = 'drug' | 'nda' | 'status' | 'count' | 'docs' | 'date';
type SortDir = 'asc' | 'desc';

interface SubmissionGroup {
  readonly key: string;
  readonly nda: string;
  readonly drugName: string;
  readonly applicant: string;
  readonly region: string;
  readonly sequences: readonly Submission[];
  readonly latest: Submission;
}

function buildGroups(rows: readonly Submission[]): SubmissionGroup[] {
  const map = new Map<string, Submission[]>();
  for (const s of rows) {
    if (s['ParallelGroupId']) continue;
    const key = s.DashboardGroupKey ?? applicationIdentityKey({ ...s, NdaNumber: s.NdaNumber ?? null, DrugName: s.DrugName ?? null });
    const existing = map.get(key);
    if (existing) existing.push(s);
    else map.set(key, [s]);
  }
  const out: SubmissionGroup[] = [];
  for (const [key, sequences] of map.entries()) {
    const sorted = [...sequences].sort((a, b) =>
      (a.SequenceNumber ?? '').localeCompare(b.SequenceNumber ?? ''),
    );
    const latest = sorted[sorted.length - 1] ?? sorted[0]!;
    const applicantRaw = latest['Applicant'];
    // Phase 5.X-dashboard-totaldocs — sum TotalDocs across all sequences in
    // the group. SQLite returns COUNT(*) as a string; coerce with Number().
    out.push({
      key,
      nda: latest.NdaNumber ?? '—',
      drugName: latest.DrugName,
      applicant: typeof applicantRaw === 'string' ? applicantRaw : '',
      region: String(latest.Region),
      sequences: sorted,
      latest,
    });
  }
  return out;
}

function loadCompactPref(): boolean {
  try {
    return globalThis.localStorage?.getItem(COMPACT_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveCompactPref(value: boolean): void {
  try {
    globalThis.localStorage?.setItem(COMPACT_LS_KEY, String(value));
  } catch {
    /* ignore — Safari private mode etc. */
  }
}

export function HomePage(): ReactElement {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => subscribeSubmissionRefresh(() => setReloadKey((key) => key + 1)), []);
  // Phase dashboard-performance-fix (TODO-042) — total number of matching
  // submissions from the server (ignoring limit/offset). Used for totalPages.
  const [serverTotal, setServerTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [resultSearch, setResultSearch] = useState('');
  const [searchPending, setSearchPending] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [regionFilter, setRegionFilter] = useState<string>('All');
  const [compactMode, setCompactMode] = useState<boolean>(loadCompactPref);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortCol, setSortCol] = useState<SortCol>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  // Phase 5.X-submission-reorder: which NDA group key has its
  // ReorderModal open, or null when closed.
  const [reorderOpenForKey, setReorderOpenForKey] = useState<string | null>(null);

  // Phase 5.X-lifecycle-report-panel: which submission has its
  // lifecycle history modal open, or null when closed.
  const [lifecycleSubmission, setLifecycleSubmission] = useState<Submission | null>(null);

  // Phase status-tracking-drawer (TODO-026): which submission has its
  // StatusDrawer open, or null when closed.
  const [selectedForStatus, setSelectedForStatus] = useState<Submission | null>(null);

  // Phase submission-archive: show/hide archived submissions toggle.
  const [showArchived, setShowArchived] = useState(false);
  // The submission pending archive/unarchive confirmation, or null.
  const [archiveTarget, setArchiveTarget] = useState<Submission | null>(null);

  // Phase attention-surface-setting — load merged user settings to check
  // the showAttentionSurface toggle. Default false until resolved.
  const [showAttentionSurface, setShowAttentionSurface] = useState(false);
  useEffect(() => {
    let cancelled = false;
    v2.settings
      .listAll()
      .then((res) => {
        if (cancelled) return;
        const val = res.settings['showAttentionSurface']?.Value;
        setShowAttentionSurface(val === true);
      })
      .catch(() => {
        // Degrades gracefully — surface stays hidden on error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase dashboard-attention-surface (TODO-024) — aggregate "needs your
  // attention" feed; only fetched when showAttentionSurface setting is on.
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  useEffect(() => {
    if (!showAttentionSurface) return;
    let cancelled = false;
    v2.dashboard
      .attentionItems()
      .then((data) => {
        if (!cancelled) setAttentionItems(data);
      })
      .catch(() => {
        if (!cancelled) setAttentionItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey, showAttentionSurface]);

  // Phase dashboard-performance-fix (TODO-042) — pass filter params to the
  // API so the server filters and counts. The frontend only receives the
  // current page's rows; pagination is server-driven via pagination.total.
  useEffect(() => {
    let stale = false;
    setSearchPending(true);
    setError(null);
    const params: Parameters<typeof v2.submissions.list>[0] = {
      groupBy: 'application',
      limit: DASH_PAGE_SIZE,
      offset: (page - 1) * DASH_PAGE_SIZE,
      ...(regionFilter !== 'All' && { region: regionFilter }),
      ...(statusFilter !== 'All' && { status: statusFilter }),
      ...(debouncedSearch.trim() !== '' && { search: debouncedSearch.trim() }),
      // phase-dashboard-coherence item 6 (D-050) — archived rows are ALWAYS
      // fetched; "Show hidden" is now a pure DISPLAY filter. This is what
      // lets the parent row's face fall back to the highest VISIBLE sequence,
      // keeps the sequence-count annotation truthful to the record
      // ("3 sequences (1 hidden)"), and renders the explicit all-hidden
      // parent state instead of silently dropping the whole group.
      includeArchived: true,
    };
    v2.submissions
      .list(params)
      .then((result) => {
        if (stale) return;
        setSubmissions(result.data);
        setResultSearch(debouncedSearch);
        setSearchPending(false);
        setServerTotal(result.pagination.total);
      })
      .catch((err: unknown) => {
        if (stale) return;
        setSearchPending(false);
        setError(err instanceof ApiError ? err.message : 'Failed to load submissions');
        setSubmissions((previous) => previous ?? []);
      });
    return () => { stale = true; };
  }, [reloadKey, page, regionFilter, statusFilter, debouncedSearch]);

  // Reset to page 1 whenever filters change so users don't end up on an
  // empty page after narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, regionFilter, sortCol, sortDir, showArchived]);

  // Phase submission-archive: execute confirmed archive/unarchive.
  const handleArchiveConfirm = (): void => {
    if (!archiveTarget) return;
    const target = archiveTarget;
    const newState = !target.IsArchived;
    setArchiveTarget(null);
    v2.submissions.archive(target.Id, newState).then(() => {
      setReloadKey((k) => k + 1);
    }).catch(() => {
      // Reload anyway to show current server state.
      setReloadKey((k) => k + 1);
    });
  };

  const toggleCompact = (): void => {
    setCompactMode((prev) => {
      const next = !prev;
      saveCompactPref(next);
      return next;
    });
  };

  const handleSort = (col: SortCol): void => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortIcon = (col: SortCol): string =>
    sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const groups = useMemo(() => buildGroups(submissions ?? []), [submissions]);

  // Phase 7.6 — partition parallel submissions (those with a ParallelGroupId)
  // into display groups, distinct from the per-NDA/drug sequence groups above.
  const parallelGroups = useMemo(() => {
    const byGroup = new Map<string, Submission[]>();
    for (const s of submissions ?? []) {
      const gid = typeof s['ParallelGroupId'] === 'string' ? s['ParallelGroupId'] : null;
      if (!gid) continue;
      const existing = byGroup.get(gid);
      if (existing) existing.push(s);
      else byGroup.set(gid, [s]);
    }
    return Array.from(byGroup.entries()).map(([groupId, subs]) => ({
      groupId,
      drugName: subs[0]?.DrugName ?? '—',
      submissions: subs,
    }));
  }, [submissions]);

  // Phase dashboard-performance-fix — filter dropdowns built from the current
  // page's data. Server handles the real filtering; these dropdowns drive what
  // params are sent and provide options visible on the current page.
  const availableStatuses = useMemo(() => {
    const s = new Set<string>(Object.keys(STATUS_CONFIG));
    for (const sub of submissions ?? []) s.add(sub.Status || 'Draft');
    return Array.from(s).sort();
  }, [submissions]);

  const availableRegions = useMemo(() => {
    const r = new Set<string>(REGIONS);
    for (const sub of submissions ?? []) r.add(String(sub.Region));
    return Array.from(r).sort();
  }, [submissions]);

  // Phase dashboard-performance-fix — secondary client-side filter applied on
  // top of server results. In production the server already returns only
  // matching rows, so this is a no-op; it ensures correct behaviour when the
  // API mock in tests doesn't honour filter params.
  const filtered = useMemo(() => {
    if (!submissions) return [];
    const q = resultSearch.trim().toLowerCase();
    return groups.filter((g) => {
      // Server-selected groups contain all members, including non-matching latest rows.
      if (g.sequences.some((s) => s.DashboardGroupKey !== undefined)) return true;
      if (regionFilter !== 'All' && g.region !== regionFilter) return false;
      if (statusFilter !== 'All' && !g.sequences.some((s) => (s.Status || 'Draft') === statusFilter))
        return false;
      if (q) {
        const hay = `${g.drugName} ${g.nda} ${g.applicant}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [submissions, groups, regionFilter, statusFilter, resultSearch]);

  const sorted = useMemo(() => {
    const cmp = (a: SubmissionGroup, b: SubmissionGroup): number => {
      let va: string | number;
      let vb: string | number;
      if (sortCol === 'drug') {
        va = a.drugName;
        vb = b.drugName;
      } else if (sortCol === 'nda') {
        va = a.nda;
        vb = b.nda;
      } else if (sortCol === 'status') {
        va = a.latest.Status ?? '';
        vb = b.latest.Status ?? '';
      } else if (sortCol === 'count') {
        va = a.sequences.length;
        vb = b.sequences.length;
      } else if (sortCol === 'docs') {
        // phase-dashboard-honest-counts — sort by the DISPLAYED number (the
        // visible-children sum), not a hidden-inclusive total.
        va = computeParentFace(a.sequences, showArchived).visibleDocs ?? -1;
        vb = computeParentFace(b.sequences, showArchived).visibleDocs ?? -1;
      } else {
        va = a.latest.UpdatedAt;
        vb = b.latest.UpdatedAt;
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    };
    return [...filtered].sort(cmp);
  }, [filtered, sortCol, sortDir, showArchived]);

  // Phase dashboard-performance-fix — totalPages uses serverTotal (the full
  // count of matching rows from the API), not the length of the fetched page.
  // All fetched rows are already the current page — no client-side slicing.
  const totalPages = Math.max(1, Math.ceil(serverTotal / DASH_PAGE_SIZE));
  const paged = sorted;
  const statusIds = paged.flatMap((group) => {
    const parent = computeParentFace(group.sequences, showArchived);
    return (expanded[group.key] ? parent.visible : parent.face ? [parent.face] : []).map((item) => item.Id);
  });
  const statusEvidence = useStatusEvidence(statusIds, submissions);

  const filtersActive = search !== '' || statusFilter !== 'All' || regionFilter !== 'All';
  const clearFilters = (): void => {
    setSearch('');
    setStatusFilter('All');
    setRegionFilter('All');
  };

  // fix/dashboard-empty-state (Test #4) — the designed zero-submission state.
  // Shown ONLY after a successful list response with total 0 and NO filter or
  // search applied (a filtered-to-nothing result keeps the table + toolbar and
  // its "No submissions match…" row). Loading (`submissions === null`) and
  // error states never flash it. The create / import actions are gated on
  // Submissions:write — the permission POST /submissions and the import commit
  // require (the side menu itself does not gate these items, so a read-only
  // identity would otherwise be offered routes that 403).
  //
  // pr-27-permission-state — when the /users/me lookup itself FAILED
  // (perms.failed) the permissions are UNKNOWN, not "none". We must NOT fall
  // through to the "ask an administrator" denial text in that case — that
  // tells an authorized writer they lack access whenever the backend blips.
  // The unknown state renders its own honest message + Retry below, so the
  // denial branch is reached ONLY for a cleanly loaded identity (see the
  // empty-state children). `canCreate` stays false while failed; the render
  // branches on `perms.failed` before the denial so the two never collide.
  const perms = useUserPermissions();
  const canCreate = !perms.loading && !perms.failed && perms.hasPermission('Submissions', 'write');
  const showEmptyState =
    submissions !== null && error === null && serverTotal === 0 && !filtersActive;

  // Action click — submission detail page for now (Wave C will refine).
  const handleAction = (s: Submission): void => {
    navigate(`/submissions/${s.Id}`);
  };

  // Phase dashboard-performance-fix — totalSubmissions is the server total.
  // (fix/dashboard-empty-state — `totalSubmissions` was only read by the old
  // in-table zero-case message; the empty-state decision reads `serverTotal`
  // directly above.)
  // phase-dashboard-v1-parity — V1's toolbar caption is terse: when a status
  // filter is active it counts the matching submissions ("N submissions"),
  // otherwise it counts the application groups ("N applications"). Match that
  // rather than V2's "Showing X of Y submissions (N applications)".
  // Grouped pagination counts complete applications and parallel groups globally.
  const countCaption = `${serverTotal} application${serverTotal !== 1 ? 's' : ''}`;

  // Phase 5.X-v1-parity-polish — count submissions in Confirm status so the
  // ReviewClassificationsCTA can decide whether to render. We surface the
  // first such submission ID as the deep-link target for "Review now".
  const confirmSubmissions = useMemo(() => {
    return (submissions ?? []).filter((s) => normalizeStatus(s.Status) === 'Confirm');
  }, [submissions]);

  // -------- Render branches -------- //

  // phase-dashboard-v1-parity — no loading early-return. V1 keeps the full
  // dashboard chrome (title + toolbar + table headers) visible during load
  // and shows a single "Loading submissions..." row in the table body, so the
  // layout doesn't flash a bare centred spinner. The loading row is rendered
  // in the table body below when `submissions === null`.

  if (error) {
    return (
      <section className="workspace-home">
        <div role="alert" style={{ color: '#b91c1c', marginBottom: 16 }}>
          {error}
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // fix/dashboard-empty-state (Test #4) — zero submissions, no filters, load
  // finished: replace the toolbar + table with a centred, designed empty
  // state that carries the routes to create or import. The page title and
  // subtitle stay above it. The "Needs your attention" panel is deliberately
  // NOT rendered here: with zero submissions the feed has nothing to say, so
  // there is no orphaned heading alongside the empty state. (This supersedes
  // the V1 in-table message row for the zero case only; the filtered-to-nothing
  // case below keeps V1's table + row.)
  if (showEmptyState) {
    return (
      <section className="workspace-home">
        <div style={{ marginBottom: 24 }}>
          <p className="workspace-eyebrow">SUBMISSION WORKSPACE</p>
          <h2 className="screen-title" style={{ margin: '0 0 6px 0' }}>
            Submission Dashboard
          </h2>
          <div className="screen-subtitle" style={{ marginBottom: 0 }}>
            Manage and track all eCTD submissions
          </div>
        </div>
        <EmptyState
          testId="dashboard-empty-state"
          icon="📂"
          heading="No submissions yet"
          subtext="Create your first eCTD submission, or import an existing eCTD package."
        >
          {perms.loading ? null : perms.failed ? (
            /* pr-27-permission-state — the /users/me lookup failed, so we
               cannot tell whether this user may create submissions. Render an
               honest UNKNOWN state with a Retry (perms.refresh re-fetches),
               NEVER the "ask an administrator" denial — that would falsely
               tell an authorized writer they lack access during a backend
               blip. On a successful retry this collapses to the create links
               or the genuine denial, whichever is true. */
            <div
              data-testid="empty-state-perms-unknown"
              style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}
            >
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                We couldn’t confirm your access just now. Your permissions are
                unknown, not denied.
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="empty-state-perms-retry"
                onClick={() => {
                  void perms.refresh();
                }}
                style={{ fontSize: 13, padding: '6px 14px' }}
              >
                Retry
              </button>
            </div>
          ) : canCreate ? (
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Link
                to="/submissions/new"
                className="btn btn-primary"
                data-testid="empty-state-create"
                style={{ textDecoration: 'none' }}
              >
                + New Submission
              </Link>
              <Link
                to="/import"
                className="btn btn-ghost"
                data-testid="empty-state-import"
                style={{ textDecoration: 'none' }}
              >
                Import eCTD package
              </Link>
            </div>
          ) : (
            <p
              data-testid="empty-state-no-access"
              style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}
            >
              Ask an administrator for access to create or import submissions.
            </p>
          )}
        </EmptyState>
      </section>
    );
  }

  return (
    <section className="workspace-home">
      {/* phase-ui-remove-dashboard-import-button (Sharon, 2026-07-15) — the
          "↑ Import existing submission" button used to sit at the right of this
          header. Importing is a rare one-time setup task, not everyday work,
          and it stays fully available from the nav menu (Workspace → Import,
          the /import route). The header is now heading-only. */}
      <div style={{ marginBottom: 24 }}>
        {/* phase-v1-ui-parity — match V1 HomeScreen heading: .screen-title /
            .screen-subtitle classes (22px/700 + 14px muted) rather than a
            raw <h2>/<p>. Kept on an <h2> (V1 uses a plain <div>) so the
            accessible heading role survives — the class drives the visual,
            which is what V1 parity requires; the tag is an a11y superset. */}
        <p className="workspace-eyebrow">SUBMISSION WORKSPACE</p>
          <h2 className="screen-title" style={{ margin: '0 0 6px 0' }}>
          Submission Dashboard
        </h2>
        <div className="screen-subtitle" style={{ marginBottom: 0 }}>
          Manage and track all eCTD submissions
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => {
          statusEvidence.refresh();
          setReloadKey((key) => key + 1);
        }} style={{ marginTop: 8 }}>Refresh</button>
      </div>

      <ReviewClassificationsCTA
        confirmCount={confirmSubmissions.length}
        firstConfirmSubmissionId={confirmSubmissions[0]?.Id ?? null}
      />

      {/* Phase dashboard-attention-surface (TODO-024) — only renders when
          the showAttentionSurface user setting is on AND the feed has items.
          When the setting is off, no API call is made either. */}
      {showAttentionSurface && <AttentionSurface items={attentionItems} />}

      {/* Toolbar: filters + compact toggle + clear + count */}
      <div className="workspace-filters"
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* phase-dashboard-v1-parity — match V1's search input: type="text",
            "..." placeholder, 8px/14px padding, 8px radius, explicit
            colour/background/outline. aria-label kept (a11y superset). */}
        <input
          type="text"
          placeholder="Search drug name, NDA, applicant..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search submissions"
          style={{
            flex: 1,
            minWidth: 220,
            padding: '8px 14px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            color: 'var(--text)',
            background: 'var(--surface)',
            outline: 'none',
          }}
        />
        {/* phase-dashboard-v1-parity — V1's select style (selStyle): 7px/12px
            padding, 13px font, explicit colour/background; "All Statuses" /
            "All Regions" capitalised exactly as V1. */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Status filter"
          style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none' }}
        >
          <option value="All">All Statuses</option>
          {availableStatuses.map((s) => (
            <option key={s} value={s}>
              {getStatusConfig(s).label}
            </option>
          ))}
        </select>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          aria-label="Region filter"
          style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none' }}
        >
          <option value="All">All Regions</option>
          {availableRegions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        {/* phase-dashboard-v1-parity — V1's Compact toggle: 11px font + a
            0.15s transition on the active/inactive state change. */}
        <button
          type="button"
          onClick={toggleCompact}
          aria-pressed={compactMode}
          style={{
            fontSize: 11,
            padding: '6px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            background: compactMode ? 'var(--primary)' : 'var(--surface)',
            color: compactMode ? '#fff' : 'var(--text-muted)',
            border: '1px solid ' + (compactMode ? 'var(--primary)' : 'var(--border)'),
            transition: 'all 0.15s',
          }}
        >
          Compact
        </button>
        {/* Phase submission-archive: toggle archived submissions */}
        <button
          type="button"
          data-testid="show-archived-toggle"
          aria-pressed={showArchived}
          onClick={() => setShowArchived((v) => !v)}
          style={{
            fontSize: 12,
            padding: '6px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            background: showArchived ? '#6b7280' : 'var(--surface)',
            color: showArchived ? '#fff' : 'var(--text-muted)',
            border: '1px solid ' + (showArchived ? '#6b7280' : 'var(--border)'),
          }}
        >
          📥 {showArchived ? 'Hide hidden submissions' : 'Show hidden'}
        </button>
        {/* phase-dashboard-v1-parity — V1's clear control: the shared
            `btn btn-ghost` class and the label "Clear" (not "Clear filters"). */}
        {filtersActive && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={clearFilters}
            style={{ fontSize: 12, padding: '6px 12px' }}
          >
            Clear
          </button>
        )}
        {/* Global count of complete server-selected application groups. */}
        <span
          data-testid="dashboard-count-caption"
          style={{ fontSize: 12, color: 'var(--text-muted)' }}
        >
          {countCaption}
        </span>
      </div>

      {searchPending && submissions !== null && <p role="status">Updating results…</p>}
      <div className="table-wrap" aria-busy={searchPending} role="region" aria-label="Submissions table" tabIndex={0}>
        {/* phase-dashboard-v1-parity — table headers match V1 HomeScreen:
            no inline cell padding (the shared .table-wrap CSS owns spacing,
            as in V1), the Drug header is centre-aligned. Sort affordances on
            Application/Latest Status/Seq are V2 additions (test-enforced).

            phase-ui-dashboard-stable-columns-artifact-cleanup (Sharon,
            2026-07-17) — DETERMINISTIC column sizing. The old auto layout +
            per-column minWidth let content decide widths, so expanding an
            application (wider sub-row labels, the per-row eye buttons)
            resized every column and wrapped "Latest Status" onto two lines.
            `table-layout: fixed` + this colgroup make the browser size
            columns from the <col> widths alone — content can never move
            them, in either expand state. Extra viewport width distributes
            proportionally, identically in both states. Overflowing cell
            content truncates inside its column (see SubmissionSubRow's
            label cell) instead of pushing the table around. */}
        <table
          className={compactMode ? 'dashboard-compact' : ''}
          style={{ width: '100%', tableLayout: 'fixed' }}
          data-testid="dashboard-table"
        >
          <colgroup data-testid="dashboard-colgroup">
            <col style={{ width: '3%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}></th>
              <th
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'drug' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('drug')}>Drug{sortIcon('drug')}</button>
              </th>
              <th
                className="col-shaded"
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'nda' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('nda')}>Application{sortIcon('nda')}</button>
              </th>
              <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Region</th>
              <th
                className="col-shaded"
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('status')}>Latest Status{sortIcon('status')}</button>
              </th>
              <th
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'count' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('count')}>Seq{sortIcon('count')}</button>
              </th>
              <th
                className="col-shaded"
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'docs' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('docs')}>Docs{sortIcon('docs')}</button>
              </th>
              <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Next Step</th>
              <th
                className="col-shaded"
                style={{ textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}
                aria-sort={sortCol === 'date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" className="table-sort" onClick={() => handleSort('date')}>Updated{sortIcon('date')}</button>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Phase 7.6 — parallel groups appear at the top of the table */}
            {parallelGroups.map((pg) => (
              <ParallelGroupRow
                key={pg.groupId}
                drugName={pg.drugName}
                submissions={pg.submissions}
              />
            ))}
            {submissions === null ? (
              /* phase-dashboard-v1-parity — V1's in-table loading row keeps the
                 chrome visible instead of a full-page spinner. */
              <tr>
                <td
                  colSpan={9}
                  style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                >
                  Loading submissions...
                </td>
              </tr>
            ) : paged.length === 0 && parallelGroups.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                >
                  {/* fix/dashboard-empty-state — the zero-submission case now
                      renders the designed empty state above (early return), so
                      this row is the filtered-to-nothing case: table + toolbar
                      stay, and a "Clear filters" action gives a way back. */}
                  <div data-testid="dashboard-no-match">No submissions match the current filters.</div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    data-testid="dashboard-clear-filters"
                    onClick={clearFilters}
                    style={{ marginTop: 12, fontSize: 13, padding: '6px 14px' }}
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            ) : (
              paged.map((g) => {
                const isOpen = expanded[g.key] ?? false;
                // item 2 (D-051-adjacent) — the ↕ arrow is ABSENT (not greyed)
                // unless at least one child is reorderable, judged by the ONE
                // shared lock predicate the padlocks and the dialog use.
                const canReorder = g.sequences.some((s) => !isSequenceLocked(s));
                // item 6 (D-050) — ONE source computes the parent's face
                // (highest VISIBLE sequence); the row's snapshot (status, seq
                // label, updated, next step) and its row-click navigation all
                // read it, so display and navigation stay in lockstep.
                const pf = computeParentFace(g.sequences, showArchived);
                const nextStepLabel = pf.face ? nextStepFor(pf.face.Status, statusEvidence.get(pf.face.Id)) : null;
                const face = pf.face;
                return (
                  <Fragment key={g.key}>
                    <SubmissionGroupRow
                      nda={g.nda}
                      drugName={g.drugName}
                      applicant={g.applicant}
                      compact={compactMode}
                      region={g.region}
                      latestStatus={face?.Status ?? null}
                      {...(face && { statusEvidence: statusEvidence.get(face.Id) })}
                      submissionCount={g.sequences.length}
                      seqLabel={face?.SequenceNumber ?? '—'}
                      hiddenNote={pf.hiddenNote}
                      allHidden={pf.allHidden}
                      latestUpdate={face?.UpdatedAt ?? g.latest.UpdatedAt}
                      totalDocs={pf.visibleDocs}
                      isExpanded={isOpen}
                      onToggle={() =>
                        setExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))
                      }
                      {...(face !== null && {
                        onRowNavigate: () => handleAction(face),
                      })}
                      {...(nextStepLabel !== null && face !== null && {
                        nextStepLabel,
                        onNextStepClick: () => handleAction(face),
                      })}
                      {...(canReorder && {
                        onReorderClick: () => setReorderOpenForKey(g.key),
                      })}
                    />
                    {isOpen && pf.visible.length === 0 && (
                      /* item 6 (D-050) — expanding an all-hidden parent shows
                         an honest explanation, not silence. */
                      <tr data-testid={`all-hidden-note-${g.nda}`}>
                        <td
                          colSpan={9}
                          style={{ padding: '10px 24px', color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}
                        >
                          All sequences in this application are hidden — turn on
                          “Show hidden” to see them.
                        </td>
                      </tr>
                    )}
                    {isOpen &&
                      pf.visible.map((s) => (
                        <SubmissionSubRow
                          key={s.Id}
                          submission={s}
                          statusEvidence={statusEvidence.get(s.Id)}
                          nda={g.nda}
                          onActionClick={handleAction}
                          onStatusClick={() => setSelectedForStatus(s)}
                          onArchive={() => setArchiveTarget(s)}
                          canReorder={canReorder}
                          {...(canReorder && {
                            onReorderClick: () => setReorderOpenForKey(g.key),
                          })}
                        />
                      ))}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Phase 5.X-submission-reorder: render at most one modal at a time. */}
      {reorderOpenForKey !== null && (() => {
        const g = groups.find((grp) => grp.key === reorderOpenForKey);
        if (!g) return null;
        return (
          <ReorderModal
            nda={g.nda}
            drugName={g.drugName}
            sequences={g.sequences}
            onClose={() => setReorderOpenForKey(null)}
            onSuccess={() => {
              setReorderOpenForKey(null);
              setReloadKey((k) => k + 1);
            }}
          />
        );
      })()}

      {/* Phase 5.X-lifecycle-report-panel: lifecycle history modal */}
      {lifecycleSubmission !== null && (
        <LifecycleReportPanel
          submissionId={lifecycleSubmission.Id}
          submissionLabel={`${lifecycleSubmission.DrugName} — Seq ${lifecycleSubmission.SequenceNumber ?? '—'}`}
          onClose={() => setLifecycleSubmission(null)}
        />
      )}

      {/* Phase status-tracking-drawer (TODO-026): slide-in drawer for status history */}
      {selectedForStatus !== null && (
        <StatusDrawer
          submission={selectedForStatus}
          onClose={() => setSelectedForStatus(null)}
        />
      )}

      {/* Phase submission-archive: confirm dialog */}
      {archiveTarget !== null && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="dashboard.archiveConfirm"
          title={archiveTarget.IsArchived ? 'Unhide this submission?' : 'Hide this submission?'}
          testId="archive-confirm-dialog"
          onClose={() => setArchiveTarget(null)}
          width={440}
          zIndex={1000}
        >
          <>
            <p style={{ margin: '0 0 20px 0', fontSize: 13, color: 'var(--text-muted)' }}>
              {archiveTarget.IsArchived
                ? `${archiveTarget.DrugName} will be restored to your active dashboard.`
                : `${archiveTarget.DrugName} will be hidden from your dashboard. You can show it again at any time using the “Show hidden” toggle.`}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                data-testid="archive-cancel-btn"
                onClick={() => setArchiveTarget(null)}
                style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="archive-confirm-btn"
                onClick={handleArchiveConfirm}
                style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 6, border: 'none', background: '#6b7280', color: '#fff' }}
              >
                {archiveTarget.IsArchived ? 'Unhide' : 'Hide'}
              </button>
            </div>
          </>
        </MovableDialog>
      )}
    </section>
  );
}
